import dynamic from 'next/dynamic';
import { Controller } from 'react-hook-form';

import { useFinanceCalculator } from './FinanceCalculator.hook';

import { Panel } from '../Panels/shared/Panel/Panel';
import { Spacer } from '../../../../helpers/genericStyles';
import { Select } from '../../../../components/Toolkit/Inputs/Select';
import { Text } from '../../../../components/ToolkitV2/Text/Text';
import { FinanceSummary } from '../FinanceSummary/FinanceSummary';
import { PriceField } from '../../../../components/CarFinancePage/financeProviders/shared/PriceField/PriceField';
import { TextWithHelpIcon } from '../Panels/shared/TextWithHelpIcon/TextWithHelpIcon';

import * as Styled from './FinanceCalculator.styled';

import type { FinanceCalculatorProps } from './FinanceCalculator.typed';
import { type CashDepositModalProps } from '../CashDepositModal/CashDepositModal';
import { type TradeInValueModalProps } from '../TradeInValueModal/TradeInValueModal';
import { type OutstandingFinanceModalProps } from '../OutstandingFinanceModal/OutstandingFinanceModal';
import { type TotalDepositModalProps } from '../TotalDepositModal/TotalDepositModal';

const DynamicCashDepositModal = dynamic<CashDepositModalProps>(
  import('../CashDepositModal/CashDepositModal').then(
    (mod) => mod.CashDepositModal,
  ),
  { ssr: false },
);

const DynamicTradeInValueModal = dynamic<TradeInValueModalProps>(
  import('../TradeInValueModal/TradeInValueModal').then(
    (mod) => mod.TradeInValueModal,
  ),
  { ssr: false },
);

const DynamicOutstandingFinanceModal = dynamic<OutstandingFinanceModalProps>(
  import('../OutstandingFinanceModal/OutstandingFinanceModal').then(
    (mod) => mod.OutstandingFinanceModal,
  ),
  { ssr: false },
);

const DynamicTotalDepositModal = dynamic<TotalDepositModalProps>(
  import('../TotalDepositModal/TotalDepositModal').then(
    (mod) => mod.TotalDepositModal,
  ),
  { ssr: false },
);

const FinanceCalculator = (props: FinanceCalculatorProps) => {
  const {
    dealerId,
    financeHouse,
    minimumDepositValue,
    minimumDepositPercentage,
    showOutstandingFinance = false,
  } = props;

  const {
    price,
    control,
    getValues,
    formattedMinimumDepositValue,
    onChangeHandlerFinanceInputs,
    tradeInValue,
    outstandingFinance,
    cashDeposit,
    onBlurFinanceInputs,
    isCashDepositModalOpen,
    setIsCashDepositModalOpen,
    isTradeInValueModalOpen,
    setIsTradeInValueModalOpen,
    isTotalDepositModalOpen,
    setIsTotalDepositModalOpen,
    isOutstandingFinanceModalOpen,
    setIsOutstandingFinanceModalOpen,
    isMonthlyRepaymentsDisabled,
  } = useFinanceCalculator({
    minimumDepositValue,
    minimumDepositPercentage,
  });

  return (
    <Panel mb={['S8', 'M16']}>
      <Text forwardedAs="h2" size="L18" weight="bold">
        Calculate Monthly Repayments
      </Text>
      <Spacer marginBottom={'M24'} />
      <Styled.InputsContainer>
        <Controller
          name="carDetails.price"
          control={control}
          render={({ field, fieldState }) => (
            <PriceField
              {...field}
              {...fieldState}
              readOnly
              label="Retail Price*"
            />
          )}
        />
        <Controller
          name="loanDetails.cashDeposit"
          control={control}
          render={({ field, fieldState }) => (
            <PriceField
              {...field}
              {...fieldState}
              onChange={(value: number | undefined) => {
                field.onChange(value);
                onChangeHandlerFinanceInputs({
                  cashDeposit: value,
                  tradeInValue,
                  outstandingFinance,
                  fieldName: field.name,
                });
              }}
              onBlur={onBlurFinanceInputs(field.name)}
              label={
                <TextWithHelpIcon
                  text="Cash Deposit*"
                  onClickIcon={() => setIsCashDepositModalOpen(true)}
                  aria-label="Cash deposit modal button"
                />
              }
            />
          )}
        />
        <Controller
          name="loanDetails.tradeInValue"
          control={control}
          render={({ field, fieldState }) => (
            <PriceField
              {...field}
              {...fieldState}
              onChange={(value: number | undefined) => {
                field.onChange(value);
                onChangeHandlerFinanceInputs({
                  cashDeposit,
                  tradeInValue: value,
                  outstandingFinance,
                  fieldName: field.name,
                });
              }}
              onBlur={onBlurFinanceInputs(field.name)}
              label={
                <TextWithHelpIcon
                  text="Estimated Trade-In Value"
                  onClickIcon={() => setIsTradeInValueModalOpen(true)}
                  aria-label="Trade-in value modal button"
                />
              }
            />
          )}
        />
        {showOutstandingFinance && (
          <Controller
            name="loanDetails.outstandingLoan"
            control={control}
            render={({ field, fieldState }) => (
              <PriceField
                {...field}
                {...fieldState}
                onChange={(value: number | undefined) => {
                  field.onChange(value);
                  onChangeHandlerFinanceInputs({
                    cashDeposit,
                    tradeInValue,
                    outstandingFinance: value,
                    fieldName: field.name,
                  });
                }}
                onBlur={onBlurFinanceInputs(field.name)}
                label={
                  <TextWithHelpIcon
                    text="Outstanding Finance"
                    onClickIcon={() => setIsOutstandingFinanceModalOpen(true)}
                    aria-label="Outstanding finance modal button"
                  />
                }
              />
            )}
          />
        )}
        <Controller
          name="loanDetails.totalDeposit"
          control={control}
          render={({ field, fieldState }) => (
            <PriceField
              {...field}
              {...fieldState}
              helpText={
                <Text size="B12">
                  Minimum deposit is{' '}
                  <strong>
                    {formattedMinimumDepositValue} ({minimumDepositPercentage}%)
                  </strong>
                </Text>
              }
              readOnly
              data-testid-error-message="total-deposit-error-message"
              label={
                <TextWithHelpIcon
                  text="Total Deposit*"
                  onClickIcon={() => setIsTotalDepositModalOpen(true)}
                  aria-label="Total deposit modal button"
                />
              }
            />
          )}
        />
        <Controller
          name="loanDetails.financeAmount"
          control={control}
          render={({ field, fieldState }) => (
            <PriceField
              {...field}
              {...fieldState}
              readOnly
              data-testid-error-message="finance-amount-error-message"
              label="Finance Amount*"
            />
          )}
        />
        <Controller
          name="loanDetails.monthlyInstallments"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              value={value?.toString()}
              onChange={(value) => {
                onChange(Number(value));
              }}
              options={getValues('loanDetails.monthlyInstallmentOptions')?.map(
                (option) => ({
                  label: option.displayValue,
                  value: option.value.toString(),
                }),
              )}
              label="Select a Term*"
              disabled={isMonthlyRepaymentsDisabled}
            />
          )}
        />
        <Controller
          name="loanDetails.pricePerMonth"
          control={control}
          render={({ field, fieldState }) => (
            <PriceField
              {...field}
              {...fieldState}
              readOnly
              label="Price Per Month*"
            />
          )}
        />
      </Styled.InputsContainer>
      <Spacer marginBottom={'M24'} />
      <FinanceSummary
        price={price}
        dealerId={dealerId}
        financeHouse={financeHouse}
      />
      <DynamicCashDepositModal
        isOpen={isCashDepositModalOpen}
        onRequestClose={() => setIsCashDepositModalOpen(false)}
      />
      <DynamicTradeInValueModal
        isOpen={isTradeInValueModalOpen}
        onRequestClose={() => setIsTradeInValueModalOpen(false)}
      />
      <DynamicTotalDepositModal
        isOpen={isTotalDepositModalOpen}
        onRequestClose={() => setIsTotalDepositModalOpen(false)}
      />
      <DynamicOutstandingFinanceModal
        isOpen={isOutstandingFinanceModalOpen}
        onRequestClose={() => setIsOutstandingFinanceModalOpen(false)}
      />
    </Panel>
  );
};

export { FinanceCalculator };
